import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Users, Fingerprint, Search, UserPlus, BadgePercent, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";

const NUEVO = { nombre: "", telefono: "", tipo: "normal" };

export default function ClienteSelector({ open, onClose, onSelect, minimos }) {
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [nuevo, setNuevo] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { if (open) { setQ(""); setResultados([]); setNuevo(null); setTimeout(() => inputRef.current?.focus(), 50); } }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const term = q.trim();
    if (term.length < 2) { setResultados([]); return undefined; }
    const t = setTimeout(async () => {
      setBuscando(true);
      try { const { data } = await api.get("/clientes", { params: { q: term } }); setResultados(data.slice(0, 12)); } catch { setResultados([]); } finally { setBuscando(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  async function seleccionar(payload) {
    try {
      const { data } = await api.post("/clientes/validar-huella", payload);
      onSelect(data);
      if (data.mayorista && data.huella_requerida && !data.huella_verificada) toast.info(`Mayorista ${data.cliente.nombre} · esta sucursal exige huella para aplicar mayoreo`);
      else if (data.mayorista) toast.success(`Mayorista ${data.cliente.nombre} · precio mayoreo · ${data.requisito}`);
      else toast.info(`Cliente ${data.cliente.nombre} · precio público`);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function onEnter(e) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const term = q.trim();
    if (/^HU-/i.test(term)) return seleccionar({ codigo: term.toUpperCase() });
    if (resultados.length) seleccionar({ cliente_id: resultados[0].id });
  }

  async function crear(e) {
    e.preventDefault();
    try {
      const { data } = await api.post("/clientes", { ...nuevo, email: "", codigo_huella: "", notas: "" });
      toast.success(data.tipo === "mayorista" ? `Mayorista registrado · huella ${data.codigo_huella}` : "Cliente registrado");
      await seleccionar({ cliente_id: data.id });
    } catch (err) { toast.error(formatApiError(err)); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="cliente-selector-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl font-bold flex items-center gap-2"><Users className="w-5 h-5 text-brand-amber" /> ¿A quién le vendes?</DialogTitle></DialogHeader>
        {!nuevo ? (
          <div className="space-y-3">
            <button data-testid="cliente-publico-general" onClick={() => { onSelect(null); onClose(); }} className="w-full flex items-center gap-3 rounded-xl border-2 border-brand-ink bg-brand-ink text-white px-4 py-3 text-left hover:bg-brand-graphite transition-colors">
              <Users className="w-5 h-5 text-brand-amber" /><div><div className="font-bold text-sm">Público en general</div><div className="text-xs text-white/60">Sin registro · precio público</div></div><span className="ml-auto font-mono text-[10px] text-white/50">Esc</span>
            </button>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3.5 text-brand-muted" />
              <input ref={inputRef} data-testid="cliente-buscar-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onEnter} placeholder="Nombre, teléfono o huella HU-…" className="field h-11 pl-9 pr-9" />
              {buscando ? <Loader2 className="w-4 h-4 absolute right-3 top-3.5 animate-spin text-brand-muted" /> : <Fingerprint className="w-4 h-4 absolute right-3 top-3.5 text-brand-muted" />}
            </div>
            <div className="max-h-64 overflow-y-auto scrollbar-thin space-y-1" data-testid="cliente-resultados">
              {resultados.map((c, i) => (
                <button key={c.id} data-testid={`cliente-opcion-${c.id}`} onClick={() => seleccionar({ cliente_id: c.id })} className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2 text-left hover:border-brand-amber hover:bg-brand-amber-soft/40 transition-colors ${i === 0 ? "border-brand-amber" : "border-brand-line"}`}>
                  <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-brand-ink truncate">{c.nombre}</div><div className="text-[11px] text-brand-muted font-mono">{c.telefono || "sin teléfono"}{c.codigo_huella ? ` · ${c.codigo_huella}` : ""}</div></div>
                  <span className={`chip h-6 px-2 ${c.tipo === "mayorista" ? "chip-amber" : ""}`}>{c.tipo === "mayorista" ? <><BadgePercent className="w-3 h-3" /> Mayoreo</> : "Público"}</span>
                  {i === 0 && <span className="font-mono text-[10px] text-brand-muted">Enter</span>}
                </button>
              ))}
              {q.trim().length >= 2 && !buscando && !resultados.length && <p className="text-sm text-brand-muted text-center py-4" data-testid="cliente-sin-resultados">Sin coincidencias para “{q}”.</p>}
            </div>
            <button data-testid="cliente-nuevo-btn" onClick={() => setNuevo({ ...NUEVO, nombre: /^HU-/i.test(q) ? "" : q.trim(), telefono: /^\d{7,}$/.test(q.trim()) ? q.trim() : "" })} className="btn-soft w-full h-10 text-sm"><UserPlus className="w-4 h-4" /> Registrar cliente nuevo</button>
          </div>
        ) : (
          <form onSubmit={crear} className="space-y-3" data-testid="cliente-nuevo-form">
            <input data-testid="cliente-nuevo-nombre" autoFocus className="field" placeholder="Nombre completo" value={nuevo.nombre} onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} required />
            <input data-testid="cliente-nuevo-telefono" className="field font-mono" placeholder="Teléfono (10 dígitos)" value={nuevo.telefono} onChange={(e) => setNuevo({ ...nuevo, telefono: e.target.value })} />
            <div className="flex gap-1 bg-brand-surface p-1 rounded-full">{[["normal", "Cliente · precio público"], ["mayorista", "Mayorista · precio mayoreo"]].map(([t, l]) => <button type="button" key={t} data-testid={`cliente-nuevo-tipo-${t}`} onClick={() => setNuevo({ ...nuevo, tipo: t })} className={`flex-1 h-9 rounded-full text-xs font-semibold ${nuevo.tipo === t ? "bg-brand-ink text-white" : "text-brand-slate"}`}>{l}</button>)}</div>
            {nuevo.tipo === "mayorista" && <p className="text-[11px] text-brand-amber-dark bg-brand-amber-soft border border-brand-amber-line rounded-xl px-3 py-2" data-testid="cliente-nuevo-politica">Política de mayoreo: primera compra ≥ ${Number(minimos?.minimo_inicial ?? 2000).toLocaleString("es-MX")} ya con precio de mayoreo; después acumular ≥ ${Number(minimos?.minimo_mensual ?? 1500).toLocaleString("es-MX")} al mes. Se generará su código de huella.</p>}
            <div className="flex gap-2"><button type="button" data-testid="cliente-nuevo-cancelar" onClick={() => setNuevo(null)} className="btn-soft h-11 px-4">Volver</button><button data-testid="cliente-nuevo-guardar" type="submit" className="btn-amber flex-1 h-11">Registrar y seleccionar</button></div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
